
import { useCallback } from "react";
import { toast } from "sonner";
import { Investor } from "@/types/investor";
import { useInvestorAI } from "@/hooks/useInvestorAI";
import { resetFailedInvestors } from "@/utils/investors/processing/processingState";

/**
 * Hook for handling batch processing operations
 */
export const useBatchOperations = (
  investors: Investor[],
  setInvestors: (investors: Investor[]) => void,
  processedIds: Set<string>,
  setProcessedIds: (processedIds: Set<string>) => void,
  contacts: any[], // Using any[] to avoid circular dependencies, but this is the Contact[] type
  flushPendingSaves: () => Promise<boolean>,
  startBatchProcessing: () => void,
  endBatchProcessing: () => Promise<boolean>
) => {
  const {
    isProcessingBatch,
    handleProcessBatch: aiHandleProcessBatch
  } = useInvestorAI();

  /**
   * Process a batch of investors
   */
  const handleProcessBatch = useCallback(async (batchSize: number, onInvestorProcessed?: (investor: Investor) => void) => {
    resetFailedInvestors();
    
    // Start batch processing mode
    startBatchProcessing();
    
    try {
      console.log(`Starting batch processing of ${batchSize} investors`);
      
      // Track starting count for debugging
      const startingCount = investors.length;
      const initialProcessedCount = processedIds.size;
      console.log(`Starting batch with ${startingCount} investors in state (${initialProcessedCount} already processed)`);
      
      // Log processedIds for debugging
      console.log(`Initial processedIds: ${Array.from(processedIds).join(', ')}`);
      
      const result = await aiHandleProcessBatch(
        batchSize,
        investors,
        localStorage.getItem("relevance_context") || "",
        processedIds,
        async (updatedInvestors) => {
          console.log(`Batch processing investor callback, saving ${updatedInvestors.length} investors to database`);
          
          if (updatedInvestors.length < startingCount) {
            console.warn(`WARNING: Investor count decreased from ${startingCount} to ${updatedInvestors.length}`);
          }
          
          // Find newly processed investors - those that now have enrichedWith but weren't in processedIds before
          const newlyProcessed = updatedInvestors.filter(inv => 
            inv.enrichedWith && !processedIds.has(inv.id)
          );
          
          if (newlyProcessed.length > 0) {
            console.log(`Found ${newlyProcessed.length} newly processed investors:`);
            console.log(newlyProcessed.map(inv => inv.id).join(', '));
          }
          
          // Update the investors array in state
          setInvestors(updatedInvestors);
          
          // Sync processedIds with actual processed investors
          const newProcessedIds = new Set(processedIds);
          updatedInvestors.forEach(inv => {
            if (inv.enrichedWith && !newProcessedIds.has(inv.id)) {
              console.log(`Adding investor ${inv.id} to processedIds`);
              newProcessedIds.add(inv.id);
            }
          });
          
          // Update processed IDs state
          setProcessedIds(newProcessedIds);
          
          // Return success - actual saving happens in the onInvestorProcessed callback for each investor
          return true;
        },
        (newProcessedIds) => {
          console.log(`Setting processed IDs, size: ${newProcessedIds.size}`);
          setProcessedIds(newProcessedIds);
        },
        async (processedInvestor) => {
          // This callback is triggered for EACH processed investor
          console.log(`Investor ${processedInvestor.id} processed during batch processing`);
          
          try {
            // Be sure to add to processedIds set immediately
            if (processedInvestor.enrichedWith && !processedIds.has(processedInvestor.id)) {
              processedIds.add(processedInvestor.id);
              console.log(`Added investor ${processedInvestor.id} to processedIds`);
              // Update processed IDs state to reflect new processed investor
              setProcessedIds(new Set(processedIds));
            }
            
            // Pass to the original callback if provided
            if (onInvestorProcessed) {
              onInvestorProcessed(processedInvestor);
            }
            
            // Return true to indicate successful processing
            return true;
          } catch (error) {
            console.error(`Error in onInvestorProcessed for ${processedInvestor.id}:`, error);
            return false;
          }
        }
      );
      
      // Log final count for debugging
      console.log(`Completed batch with ${investors.length} total investors`);
      console.log(`Final processedIds size: ${processedIds.size}`);
      
      return result;
    } catch (error) {
      console.error("Error during batch processing:", error);
      toast.error("An error occurred during batch processing");
      throw error;
    } finally {
      // End batch processing mode and ensure all data is saved
      console.log("Finalizing batch processing and ensuring all data is saved");
      await endBatchProcessing();
      
      // Force a final save to ensure everything is persisted
      await flushPendingSaves();
      
      // Additional debug check to verify final state
      const finalCount = investors.length;
      const processedCount = investors.filter(inv => inv.enrichedWith).length;
      console.log(`Batch processing complete. Current state: ${finalCount} investors, ${processedCount} processed`);
    }
  }, [
    investors,
    processedIds, 
    setInvestors, 
    setProcessedIds, 
    flushPendingSaves, 
    startBatchProcessing, 
    endBatchProcessing, 
    aiHandleProcessBatch
  ]);

  return {
    isProcessingBatch,
    handleProcessBatch
  };
};
